import type { PerfSeries, SheetRecovery } from '@agio/core';
import { SLEEVES } from '@agio/core/domain';
import { fmtNum } from '../../utils.js';
import { Table, THeadRow, TH, TD, TR, Ltr } from '../ui/table.js';

const SERIES_HE: Record<PerfSeries, string> = {
  asset_volume: 'היקף נכסים',
  deposits: 'הפקדות',
  withdrawals: 'משיכות',
  monthly_return: 'תשואה חודשית',
  cumulative_return: 'תשואה מצטברת',
  benchmark_cumulative: "בנצ'מרק מצטבר",
  benchmark_monthly: "בנצ'מרק חודשי",
};

/**
 * Sheets the rules could not read, with the reading the system proposes.
 *
 * Every figure here was read by the ordinary parser from cells a model pointed
 * at, and has passed the arithmetic checks. It is still a proposal: nothing has
 * loaded, and the admin is the one who compares it with the file and decides.
 */
export function LayoutConfirm({
  recoveries,
  busy,
  onConfirm,
  onCancel,
}: {
  recoveries: SheetRecovery[];
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div role="alert" className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
      <p className="font-semibold text-amber-900">חלק מהגיליונות לא נקראו בדרך הרגילה</p>
      <p className="mt-1 text-sm text-foreground">
        המערכת מציעה לקרוא אותם כפי שמוצג למטה. שום דבר לא נקלט עדיין: השוו את המספרים לקובץ, ואשרו רק אם הם נכונים.
      </p>
      {recoveries.map((r) => (
        <div key={r.sheet} className="mt-4 rounded border border-amber-200 bg-white p-3">
          <p className="text-sm">
            <span className="text-muted-foreground">גיליון: </span>
            <span dir="auto" className="font-medium">{r.sheet}</span>
            <span className="text-muted-foreground"> · יקרא כ: </span>
            <span className="font-medium">{r.reading.kind === 'holdings' ? 'החזקות (ריכוז)' : "תשואות (בנצ'מרק)"}</span>
            <span className="text-muted-foreground"> · תיק: </span>
            <span dir="auto" className="font-medium">{r.reading.fund}</span>
          </p>
          {r.reading.kind === 'holdings' ? (
            <HoldingsReadingView r={r} />
          ) : (
            <p className="mt-2 text-sm">
              נמצאו <span className="font-bold tabular-nums">{r.reading.rows}</span> שורות חודשיות, מ־<Ltr>{r.reading.from}</Ltr> עד <Ltr>{r.reading.to}</Ltr>.
              {' '}עמודות:{' '}
              {r.layout.performance &&
                (Object.entries(r.layout.performance.columns) as [PerfSeries, string | null][])
                  .filter(([, col]) => col !== null)
                  .map(([k, col]) => `${SERIES_HE[k]} (${col})`)
                  .join(' · ')}
            </p>
          )}
        </div>
      ))}
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" disabled={busy} onClick={onConfirm}
          className="rounded-md bg-primary px-6 py-2 font-semibold text-primary-foreground disabled:opacity-60">
          {busy ? 'קולט…' : 'המספרים נכונים — קלטו'}
        </button>
        <button type="button" disabled={busy} onClick={onCancel}
          className="rounded-md border border-input px-6 py-2 font-semibold disabled:opacity-60">
          ביטול
        </button>
      </div>
    </div>
  );
}

function HoldingsReadingView({ r }: { r: SheetRecovery }) {
  if (r.reading.kind !== 'holdings' || !r.layout.holdings) return null;
  const { reading, layout } = r;
  const totalsAgree = reading.printed_total !== null && Math.abs(reading.printed_total - reading.total_value) <= 0.5;
  return (
    <>
      <Table minWidth="20rem" className="mt-2">
        <THeadRow>
          <TH>אפיק</TH>
          <TH>תא</TH>
          <TH align="end">סכום</TH>
        </THeadRow>
        <tbody>
          {SLEEVES.filter((s) => reading.sleeves[s.column as keyof typeof reading.sleeves] !== null).map((s) => (
            <TR key={s.sleeveId}>
              <TD>{s.displayNameHe}</TD>
              <TD><Ltr>{layout.holdings!.sleeves[s.column as keyof typeof reading.sleeves]?.value_cell}</Ltr></TD>
              <TD align="end" className="tabular-nums">{fmtNum(reading.sleeves[s.column as keyof typeof reading.sleeves])}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
      <p className="mt-2 text-sm">
        <span className="text-muted-foreground">תאריך דוח: </span>
        <Ltr>{reading.report_date ?? 'לא נמצא'}</Ltr>
        <span className="text-muted-foreground"> · סה"כ: </span>
        <span className="font-bold tabular-nums">{fmtNum(reading.total_value)}</span>
        {reading.printed_total !== null && (
          <span className={totalsAgree ? 'text-emerald-700' : 'text-destructive'}>
            {totalsAgree ? ' ✓ תואם לסה"כ שבגיליון' : ' ✗ שונה מהסה"כ שבגיליון'}
          </span>
        )}
      </p>
    </>
  );
}
